import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import TrueImpactText from '../base/TrueImpactText';
import TrueImpactIconButton from './TrueImpactIconButton';
import { TrueImpactButtonWithLoading } from './TrueImpactButton';
import { Modal } from '@mui/material';
import { COLORS } from '../settings/variables/Colors';
import {
  faPrint,
  faClose,
  faMaximize,
  faMinimize,
  faSync,
  faSpinnerThird,
} from '@fortawesome/pro-light-svg-icons';
import { useReactToPrint } from 'react-to-print';
import { MEDIA_BREAKPOINTS } from '../settings/variables/Breakpoints';
import { SPACERS } from '../settings/variables/Spacers';
import { thinScrollbarStyles } from '../settings/variables/Scrollbar';
import { ButtonProps } from './TrueImpactSimpleModal';

const height = { height: '100%' };

export interface TrueImpactModalProps {
  open: boolean;
  title?: string | JSX.Element;
  errorText?: JSX.Element | string;
  maxWidth?: string;
  maxHeight?: string;
  addStyles?: string;
  keepMounted?: boolean;
  showExpandButton?: boolean;
  showPrintButton?: boolean;
  initialExpandValue?: boolean;
  hideHeader?: boolean;
  hideFooter?: boolean;
  reload?: ButtonProps;
  next?: ButtonProps;
  previous?: ButtonProps;
  onClose: () => unknown;
  children?: React.ReactNode | React.ReactNode[];
  modalContentRef?: React.RefObject<HTMLDivElement>;
}

const StyledDiv = styled.div<{
  maxWidth?: string;
  maxHeight?: string;
  addStyles?: string;
  expand: boolean;
}>`
  width: 100%;
  height: 100vh;
  transition: padding 0.5s;
  ${({ expand }) => (expand ? '' : 'padding: 80px;')}
  ${({ maxWidth, expand }) =>
    maxWidth && !expand ? `max-width: ${maxWidth}; margin: auto;` : ''}
  ${({ maxHeight, expand }) =>
    maxHeight && !expand ? `max-height: ${maxHeight}; margin: auto;` : ''}
  .modal-wrapper {
    height: 100%;
    display: flex;
    max-height: 100vh;
    border-radius: 4px;
    flex-direction: column;
    background-color: ${COLORS.whiteBase};
    ${({ expand }) =>
      expand ? 'max-height: 100vh !important; height: 100vh !important;' : ''}
    .close {
      padding: ${SPACERS.small} ${SPACERS.medium};
      text-align: right;
      svg {
        cursor: pointer;
      }
    }
    .modal-title {
      text-align: center;
      margin-top: 0 !important;
      margin-bottom: 0 !important;
      padding-top: 0 !important;
      padding: ${SPACERS.medium};
    }
    .modal-content {
      flex: 1;
      overflow: auto;
      ${thinScrollbarStyles}
      background-color: ${COLORS.grayLightest};
      transition: all 0.5s;
    }
    .modal-footer {
      padding: ${SPACERS.medium};
      box-shadow: 0px 0px 4px #ccc;
      .error-text {
        text-align: center;
        margin-bottom: ${SPACERS.tiny};
      }
      .modal-buttons-wrapper {
        display: flex;
        align-items: center;
        justify-content: center;
        .modal-previous-button {
          margin-right: ${SPACERS.tiny};
        }
        .modal-next-button {
          button {
            min-width: 80px;
          }
          margin-left: ${SPACERS.tiny};
        }
      }
    }
  }
  ${MEDIA_BREAKPOINTS.smallerThanMedium} {
    padding: 0;
  }
  ${({ addStyles }) => addStyles || ''}
`;

const getTitle = (title: string | JSX.Element | undefined) => {
  if (!title) return null;
  if (typeof title === 'string') {
    return (
      <TrueImpactText variant="heading" className="modal-title">
        {title}
      </TrueImpactText>
    );
  }
  return title;
};

export const modalHeightAutoStyles = 'display: flex; align-items: center; .modal-wrapper { flex: 1; height: auto; }';
export const modalTitleStyles = '.modal-wrapper .modal-title {margin-top: -42px !important; max-width: 80%;margin-left: ' +
  'auto;margin-right: auto;}';
export const modalRadiusStyles = `.modal-wrapper, .modal-content { border-top-right-radius: ${SPACERS.extraTiny}; ` +
  `border-top-left-radius: ${SPACERS.extraTiny}; }`;
export const modalFooterRadiusStyles = `.modal-footer { border-bottom-right-radius: ${SPACERS.extraTiny}; border-bottom-left-radius: ` +
  `${SPACERS.extraTiny}; }`;
export const modelExtraRadiusStyles = `
  .modal-wrapper {
    box-shadow: 0px 20px 36px rgba(0, 0, 0, 0.5);
  }
  .modal-content {
    border-bottom-right-radius: ${SPACERS.extraTiny};
    border-bottom-left-radius: ${SPACERS.extraTiny};
  }
`;
const TrueImpactModal: React.FC<TrueImpactModalProps> = ({
  open,
  title,
  next,
  maxWidth,
  maxHeight,
  errorText,
  keepMounted,
  addStyles,
  showPrintButton = false,
  showExpandButton = false,
  initialExpandValue = false,
  reload,
  previous,
  hideHeader,
  hideFooter,
  children,
  modalContentRef,
  onClose,
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const handlePrint = showPrintButton
    ? useReactToPrint({
        content: () => ref.current,
      })
    : undefined;

  const [expand, setExpand] = useState<boolean>(initialExpandValue);

  const handleToggleExpand = useCallback(() => {
    setExpand(o => !o);
  }, [setExpand]);

  return (
    <Modal
      open={open}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return;
        onClose();
      }}
      keepMounted={keepMounted}
      disableAutoFocus={true}
    >
      <StyledDiv
        maxWidth={maxWidth}
        maxHeight={maxHeight}
        expand={showExpandButton && expand}
        addStyles={addStyles}
      >
        <div className="modal-wrapper">
          {!hideHeader && (
            <div className="modal-header">
              <div className="close">
                {handlePrint && (
                  <TrueImpactIconButton
                    icon={faPrint}
                    color="blackBase"
                    label="Print"
                    onClick={handlePrint}
                  />
                )}
                {reload && (
                  <TrueImpactIconButton
                    icon={reload.isLoading ? faSpinnerThird : faSync}
                    label={reload.text}
                    spin={reload.isLoading}
                    disabled={reload.isLoading}
                    onClick={reload.onClick}
                    color="blackBase"
                  />
                )}
                {showExpandButton && (
                  <TrueImpactIconButton
                    icon={expand ? faMinimize : faMaximize}
                    label={expand ? 'Exit Fullscreen' : 'Expand'}
                    disabled={false}
                    onClick={handleToggleExpand}
                    color="blackBase"
                  />
                )}
                <TrueImpactIconButton
                  icon={faClose}
                  label="Close"
                  disabled={false}
                  onClick={onClose}
                  color="blackBase"
                />
              </div>
              {getTitle(title)}
            </div>
          )}
          <div ref={modalContentRef} className="modal-content">
            <div ref={ref} style={height}>
              {children}
            </div>
          </div>
          {!hideFooter && (
            <div className="modal-footer">
              {errorText && (
                <TrueImpactText color="critical" className="error-text">
                  {errorText}
                </TrueImpactText>
              )}
              <div className="modal-buttons-wrapper">
                {previous && (
                  <TrueImpactButtonWithLoading
                    disabled={previous.disabled}
                    variant="secondary-large"
                    isLoading={previous.isLoading}
                    onClick={previous.onClick}
                    color={previous.textColor}
                    colorHover={previous.textColorHover}
                    backgroundColor={previous.backgroundColor}
                    backgroundColorHover={previous.backgroundColorHover}
                    className="modal-previous-button"
                  >
                    {previous.text}
                  </TrueImpactButtonWithLoading>
                )}
                {next && (
                  <TrueImpactButtonWithLoading
                    disabled={next?.disabled}
                    onClick={next.onClick}
                    isLoading={next.isLoading}
                    color={next.textColor}
                    colorHover={next.textColorHover}
                    backgroundColor={next.backgroundColor}
                    backgroundColorHover={next.backgroundColorHover}
                    className="modal-next-button"
                  >
                    {next.text}
                  </TrueImpactButtonWithLoading>
                )}
              </div>
            </div>
          )}
        </div>
      </StyledDiv>
    </Modal>
  );
};

export default TrueImpactModal;
